// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type StoreHealthIndicator = components["schemas"]["StoreHealthIndicatorEnum"];
export type StoreHealthMetricFormat = components["schemas"]["StoreHealthMetricFormatEnum"];
export type StoreHealthMetric = components["schemas"]["StoreHealthMetricResponse"];
export type StoreHealthPanel = components["schemas"]["StoreHealthPanelResponse"];

/**
 * The seven health panels, in display order.
 *
 * One endpoint each, like the scorecard. They differ in cost by an order of magnitude —
 * boxes reads every drawn box in the store, upcs reads a counter — so a shared endpoint
 * would make every panel as slow as the slowest, and one bad query would blank all seven.
 */
export const STORE_HEALTH_PANELS = ["cameras", "boxes", "match", "mods", "upcs", "action-tool", "field-tool"] as const;

export type StoreHealthPanelName = (typeof STORE_HEALTH_PANELS)[number];

const PANEL_ENDPOINTS = {
  cameras: "/v1/stores/{store_name}/health/cameras",
  boxes: "/v1/stores/{store_name}/health/boxes",
  match: "/v1/stores/{store_name}/health/match",
  mods: "/v1/stores/{store_name}/health/mods",
  upcs: "/v1/stores/{store_name}/health/upcs",
  "action-tool": "/v1/stores/{store_name}/health/action-tool",
  "field-tool": "/v1/stores/{store_name}/health/field-tool",
} as const;

// Health is a live reading of running pipelines and nothing is cached server-side, so this
// is only long enough that switching tabs and coming straight back does not re-query. Any
// longer and the page would be quietly showing yesterday's outage.
const HEALTH_STALE_TIME = 30_000;

export function useStoreHealthPanelQuery(storeName: string, panel: StoreHealthPanelName) {
  return useQuery({
    queryKey: ["stores", "health", storeName, panel],
    staleTime: HEALTH_STALE_TIME,
    enabled: !!storeName,
    queryFn: async () => {
      const res = await api.GET(PANEL_ENDPOINTS[panel], {
        params: { path: { store_name: storeName } },
      });
      // An error status with an empty body leaves BOTH `error` and `data` undefined, so
      // testing `error` alone would resolve the query to undefined and the panel would
      // then read `.metrics` off it.
      if (res.error || !res.data) throw new Error(`Failed to load ${panel} health panel`);
      return res.data as StoreHealthPanel;
    },
  });
}
